import { fill, t, type StringKey } from '../strings';
import type { WorkOrderVisitStatus } from './visits';
import type { HeldState } from '../files/classification';

/**
 * 🔒 The Service Report as a document of the visit (piece 29; decision 2910;
 * T2184): one per visit, numbered off its job, its state read off the visit,
 * and the notes it carries. These are its shapes and the rules that need no
 * database.
 */

/** 🔒 The four states a report reads, in the order a report reaches them (decision 2910). */
export const SERVICE_REPORT_STATES = ['Open', 'Signed', 'Sent', 'Void'] as const;
export type ServiceReportState = (typeof SERVICE_REPORT_STATES)[number];

const STATE_KEYS: Readonly<Record<ServiceReportState, StringKey>> = {
  Open: 'serviceReport.state.open',
  Signed: 'serviceReport.state.signed',
  Sent: 'serviceReport.state.sent',
  Void: 'serviceReport.state.void',
};

/** A report's state as a person reads it; a visit still waiting for its first stamp reads *Not Opened Yet*. */
export function serviceReportStateLabel(state: ServiceReportState | null): string {
  return t(state === null ? 'serviceReport.state.none' : STATE_KEYS[state]);
}

/** 🔒 The report's number: the job's number and SR with the visit's number, *WO-2026-008 · SR1* (decision 2910). */
export function serviceReportNumber(workOrderNumber: string, visitNumber: number): string {
  return fill(t('serviceReport.number'), { number: workOrderNumber, visit: String(visitNumber) });
}

/** What a report's state is read from: the visit's own columns and nothing else. */
export interface ServiceReportFacts {
  status: WorkOrderVisitStatus;
  enRouteAt: Date | string | null;
  startedAt: Date | string | null;
  serviceReportFileId: string | null;
  serviceReportSentAt: Date | string | null;
}

/**
 * 🔒 The report's state (decision 2910). Void where a visit whose report had
 * opened is canceled; Sent once mailed; Signed once the sheet is frozen; Open
 * from the first On My Way, Arrived or Clock In. 🔨 A visit with no stamp has
 * opened no report, canceled or not, and reads null.
 */
export function serviceReportState(facts: ServiceReportFacts): ServiceReportState | null {
  const opened = facts.enRouteAt !== null || facts.startedAt !== null || facts.serviceReportFileId !== null;
  if (!opened) return null;
  if (facts.status === 'Cancelled') return 'Void';
  if (facts.serviceReportFileId !== null && facts.serviceReportSentAt !== null) return 'Sent';
  if (facts.serviceReportFileId !== null) return 'Signed';
  return 'Open';
}

/** 🔒 Whether the report is signed and so read as signed: the frozen sheet stands (decision 2910). */
export function serviceReportReadOnly(state: ServiceReportState | null): boolean {
  return state === 'Signed' || state === 'Sent' || state === 'Void';
}

/** 🔒 The longest note a report takes, in characters: a long paragraph, never a pasted file. */
export const VISIT_NOTE_MAX_CHARS = 4000;

/** 🔒 Decision 4320: the most pictures one note carries, each within the upload limit. */
export const VISIT_NOTE_MAX_PHOTOS = 10;

/**
 * 🔒 Why a note is refused, or null (decision 2910): a note carries words or a
 * photo, its words fit, and a finished or canceled visit's report takes none,
 * because a signed report is read as signed.
 */
export function visitNoteProblem(input: { body: string | null | undefined; fileCount: number; visitStatus: WorkOrderVisitStatus; signed: boolean }): string | null {
  const body = input.body?.trim() ?? '';
  if (body === '' && input.fileCount === 0) return 'a note carries words or a photo';
  if (body.length > VISIT_NOTE_MAX_CHARS) return `a note is at most ${VISIT_NOTE_MAX_CHARS} characters`;
  if (input.fileCount > VISIT_NOTE_MAX_PHOTOS) return `a note carries at most ${VISIT_NOTE_MAX_PHOTOS} pictures`;
  if (input.visitStatus === 'Cancelled') return 'this visit was canceled';
  if (input.signed || input.visitStatus === 'Complete') return 'this report is signed, and a signed report takes no note';
  return null;
}

/** One photo a note was added with, read on the path the visit's answer names for it (decision 13210). */
export interface VisitNotePhoto {
  fileId: string;
  filename: string;
  /** 🔒 Decision 4320: the picture's own caption, its File's description; null where it carries none. Absent from an older reply. */
  caption?: string | null;
  /** 🔒 Decision 4320 (decision 0234): why the picture does not stream yet, Scanning, Blocked or Gone; null where it streams. */
  held?: HeldState | null;
  /**
   * 🔒 Decision 13210: the path the picture opens on for this viewer, relative to the API root, decided once on the server:
   * the visit's picture route for a worker its assignment names, the file route for anyone else. Absent from an older
   * reply and from a job page's notes, which read the file route.
   */
  path?: string | null;
  /**
   * 🔒 Decision 13310: the asset the photo is linked to, through its `FileLink`; null where it is linked to none. Absent
   * from an older reply and from a job page's notes.
   */
  asset?: VisitPhotoAsset | null;
}

/** 🔒 Decision 13310: an asset node a visit photo is linked to, or one Link to Asset offers, by its id and its name. */
export interface VisitPhotoAsset {
  id: string;
  name: string;
}

/** The description a note's photo was filed under before decision 4320 when it carried no caption. */
const UNCAPTIONED_BEFORE = 'Add Photo';

/**
 * 🔒 Decision 4320: a picture's caption read off its File's description. A description equal to the
 * note's own words, or the default an uncaptioned photo was filed under before, reads as no caption,
 * so a note written before reads as it did.
 */
export function visitPhotoCaption(description: string | null | undefined, body: string | null | undefined): string | null {
  const caption = description?.trim() ?? '';
  if (caption === '' || caption === UNCAPTIONED_BEFORE || caption === (body?.trim() ?? '')) return null;
  return caption;
}

/** 🔒 Decision 4121: the job's line a note or photo was posted under, with the label the Lines Worked tile draws. */
export interface VisitNoteLine {
  lineId: string;
  label: string;
}

/** 🔒 Decision 4121: a line's label as the Lines Worked tile draws it, *Line 2 · Replace the actuator*. */
export function visitLineLabel(lineNumber: number, description: string): string {
  return `${fill(t('workOrder.visitPage.lineLabel'), { line: String(lineNumber) })} · ${description}`;
}

/** One note on the report's feed, newest last. */
export interface VisitNoteRow {
  id: string;
  authorName: string;
  createdAt: string;
  body: string | null;
  photos: VisitNotePhoto[];
  /** 🔒 Decision 4121: the line it was posted under; null on a note posted under no line, and absent from an older reply. */
  line?: VisitNoteLine | null;
}

/** 🔒 The report's own facts the visit's page reads beside the visit (decision 2910). */
export interface ServiceReportFactsAnswer {
  number: string;
  state: ServiceReportState | null;
  sentAt: string | null;
  notes: VisitNoteRow[];
  /** Whether this viewer adds a note: a worker on the visit, or a viewer holding the Work Order write, on a report still open. */
  canAddNote: boolean;
}

/** One line the viewer is on, with the one clock control it offers on the report's bar (decision 2910, through My Jobs' rule). */
export interface ServiceReportBarLine {
  lineId: string;
  label: string;
  running: { entryId: string; startedAt: string } | null;
}

/**
 * 🔒 The technician's bar (decision 2910): what the viewer's own employment may
 * press on this visit, read by My Jobs' own rules. Absent on a viewer with no
 * employment, who presses nothing.
 */
export interface ServiceReportBar {
  /** The visit's taps offered now, by `tapsOffered`. */
  taps: ('OnMyWay' | 'Arrived' | 'Leaving')[];
  /** The viewer's lines on the job, each with its running clock. */
  lines: ServiceReportBarLine[];
  /** Whether the viewer's one running clock runs on this job. */
  clockRunsHere: boolean;
  gated: boolean;
  /** Whether the viewer holds `workorder.time.typed`, which Record Time asks. */
  canRecord: boolean;
  /**
   * 🔒 Decision 4320: whether a card's Start Line stands on a line of the viewer's that their clock does not run on: the clock's
   * terms, an open visit, an open job and an ungated site, read without the rule that no clock of theirs runs on this job,
   * since starting a second line ends the first. The report's footer carries no Clock In. Absent from an older reply.
   */
  lineStart?: boolean;
  /** 🔒 T1166: On My Way's minutes, pre-filled from the drive time; null where none is known. */
  driveMinutes: number | null;
}

/** One row of the job's Documents tile (decision 2910): its sheet, a visit's report, or an invoice raised from it. */
export interface WorkOrderDocumentRow {
  kind: 'WorkOrder' | 'ServiceReport' | 'Invoice';
  id: string;
  number: string;
  /** The report's state, or the invoice's status; null on the job's own sheet. */
  state: string | null;
  /** The day the row stands on, an instant: the report's visit window or the invoice's date. */
  at: string | null;
  /** Where the row opens. */
  href: string | null;
  /** A report's frozen sheet and the version Print reads, where it is signed. */
  fileId: string | null;
  versionId: string | null;
}

/** What the job's Documents tile reads (decision 2910). */
export interface WorkOrderDocumentsAnswer {
  workOrderId: string;
  rows: WorkOrderDocumentRow[];
}
